import { useState, useEffect } from 'react';
import soapHistoryService from '../../services/soapHistory.service';
import './SoapHistory.css';

const SoapHistory = ({ setActiveTab }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setActiveTab('soap-history');
    fetchHistory();
    fetchStats();
  }, [setActiveTab, page]);

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await soapHistoryService.getAll(page, 20);

      if (response.success) {
        setHistory(response.data);
        setTotalPages(response.pagination.totalPages);
      } else {
        setError('Failed to load SOAP history');
      }
    } catch (err) {
      console.error('Error fetching SOAP history:', err);
      setError(err.message || 'Failed to load SOAP history');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await soapHistoryService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleViewDetails = async (entryId) => {
    try {
      const response = await soapHistoryService.getById(entryId);
      if (response.success) {
        setSelectedEntry(response.data);
        setShowDetailModal(true);
      }
    } catch (err) {
      console.error('Error fetching entry details:', err);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await soapHistoryService.delete(entryId);
      fetchHistory();
      fetchStats();
    } catch (err) {
      console.error('Error deleting entry:', err);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL history entries? This action cannot be undone.')) return;

    try {
      await soapHistoryService.deleteAll();
      fetchHistory();
      fetchStats();
    } catch (err) {
      console.error('Error deleting all entries:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    return `${ms}ms`;
  };

  return (
    <>
      <header className="top-header">
        <div className="breadcrumb">
          <span>Monitoring</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">AS2 Logs</span>
        </div>
      </header>
      <div className="soap-history-container">
        {/* Header */}
        <div className="soap-history-header">
          <h1 className="page-title">SOAP Request History</h1>
          <div className="header-actions">
            <button
              className="refresh-btn"
              onClick={() => { fetchHistory(); fetchStats(); }}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh
            </button>
            {history.length > 0 && (
              <button
                className="delete-all-btn"
                onClick={handleDeleteAll}
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
                Delete All
              </button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{stats.total_requests || 0}</div>
              <div className="stat-label">Total Requests</div>
            </div>
            <div className="stat-card success">
              <div className="stat-value">{stats.successful_requests || 0}</div>
              <div className="stat-label">Successful</div>
            </div>
            <div className="stat-card error">
              <div className="stat-value">{stats.failed_requests || 0}</div>
              <div className="stat-label">Failed</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.avg_duration_ms ? Math.round(stats.avg_duration_ms) + 'ms' : 'N/A'}</div>
              <div className="stat-label">Avg Duration</div>
            </div>
          </div>
        )}

        {/* History Table */}
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading history...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
          </div>
        ) : history.length === 0 ? (
          <div className="empty-state">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="48" height="48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <h3>No History Yet</h3>
            <p>SOAP requests will appear here once you start testing</p>
          </div>
        ) : (
          <>
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Vendor</th>
                    <th>Operation</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.created_at)}</td>
                      <td>
                        <span className="vendor-badge">{entry.vendor}</span>
                      </td>
                      <td>{entry.operation}</td>
                      <td>
                        <span className={`status-badge ${entry.success ? 'success' : 'error'}`}>
                          {entry.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td>{formatDuration(entry.duration_ms)}</td>
                      <td className="actions-cell">
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewDetails(entry.id)}
                          title="View Details"
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(entry.id)}
                          title="Delete"
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedEntry && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Request Details</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowDetailModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="detail-section">
                  <h3>General Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Vendor:</label>
                      <span>{selectedEntry.vendor}</span>
                    </div>
                    <div className="detail-item">
                      <label>Operation:</label>
                      <span>{selectedEntry.operation}</span>
                    </div>
                    <div className="detail-item">
                      <label>Status:</label>
                      <span className={`status-badge ${selectedEntry.success ? 'success' : 'error'}`}>
                        {selectedEntry.success ? 'Success' : 'Failed'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Duration:</label>
                      <span>{formatDuration(selectedEntry.duration_ms)}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Endpoint:</label>
                      <span className="endpoint-text">{selectedEntry.endpoint}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Timestamp:</label>
                      <span>{formatDate(selectedEntry.created_at)}</span>
                    </div>
                  </div>
                </div>

                {selectedEntry.error_message && (
                  <div className="detail-section error-section">
                    <h3>Error Message</h3>
                    <pre className="error-text">{selectedEntry.error_message}</pre>
                  </div>
                )}

                <div className="detail-section">
                  <h3>Request Data</h3>
                  <pre className="json-display">
                    {JSON.stringify(selectedEntry.request_data, null, 2)}
                  </pre>
                </div>

                {selectedEntry.response_data && (
                  <div className="detail-section">
                    <h3>Response Data</h3>
                    <pre className="json-display">
                      {JSON.stringify(selectedEntry.response_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SoapHistory;
